import api from "../api/api";
import { showToast } from "../components/toast";
import { appState, dispatch, routerHistory } from "../redux/redux-config";
import { AlertStatus, AlertType, Path, TemplateType } from "../utilities/constants";
import navigate from "../utilities/navigation";

const alerts = {
  showCreateAlert: function (isEmergency, groups, users, from) {
    let alert = {
      ...blankAlert,
      allGroups: isEmergency,
      isEmergency: isEmergency,
      callLevel: "quick"
    };
    if (groups) alert.groups = groups;
    if (users) alert.users = users;

    dispatch("NEW_ALERT", alert);

    if (isEmergency) {
      navigate(Path.SendEmergency);
    } else {
      navigate({
        pathname: Path.Send,
        state: { from: from },
      });
    }
  },

  sendMessageToUsers: async function (existingAlert, users) {
    // Create a temp group with the selected users (same pattern as People.jsx)
    let firstNames = users.map((u) => u.name.split(" ")[0].trim());
    let groupName = firstNames.join(", ");
    let userIds = users.map((u) => u.id);

    let groupData = {
      id: 0,
      name: groupName,
      description: groupName,
      groupCode: "",
      groupType: 5,
      allowOptIn: true,
      locationId: 0,
      alwaysAttach: 0,
      callerId: "",
      emergencyCallerId: "",
      groupEmail: "",
      groupEmailName: "",
      reportingEmail: "",
      reportingEmailName: "",
      logoUrl: "",
      users: users,
      is_temp: 1,
    };

    let saveResult = await api.groups.save(appState().user.token, groupData);
    if (!saveResult?.success) {
      showToast("error", "Failed to create recipient group.");
      return;
    }

    await api.people.addPeopleToGroup(appState().user.token, saveResult.id, userIds);

    let group = {
      id: saveResult.id,
      name: groupName,
      groupType: 5,
      is_temp: 1,
    };

    let data = {
      id: 0,
      name: existingAlert.name || "",
      isScheduled: false,
      scheduledDate: null,
      groups: [group],
      users: users,
      allGroups: false,
      isEmergency: false,
      isDraft: false,
      attachments: existingAlert.attachments || [],
    };

    if (existingAlert.alertType === AlertType.Text) {
      data.includeText = true;
      data.smsMessage = existingAlert.message || existingAlert.smsMessage || "";
    } else if (existingAlert.alertType === AlertType.Email) {
      data.includeEmail = true;
      data.emailMessage = existingAlert.message || existingAlert.emailMessage || "";
    } else if (existingAlert.alertType === AlertType.Voice) {
      data.includeVoice = true;
      data.voiceFileIdentifier = existingAlert.voiceFileIdentifier;
      if (!existingAlert.message) {
        data.voiceFileUrl = existingAlert.voiceFileUrl;
      } else {
        data.t2sMessage = existingAlert.message;
        data.t2sUrl = existingAlert.voiceFileUrl;
      }
    }

    dispatch("NEW_ALERT", data);
    navigate({ pathname: Path.Send, state: { from: Path.AlertDetails } });
  },

  showEditAlert: function (alert, from) {
    let data = {
      id: alert.status !== AlertStatus.Sent ? alert.id : 0,
      name: alert.name,
      isScheduled: alert.scheduledDate ? true : false,
      scheduledDate:
        (alert.status !== AlertStatus.Sent ? alert.scheduledDate : null) ||
        null,
      groups: alert.groups,
      allGroups: false, //
      isEmergency: alert.isEmergency,
      isDraft: alert.status === AlertStatus.Draft,
      attachments: alert.attachments || [],
      replyTo: alert.replyTo || "",
      emailSenderName: alert.emailSenderName || "",
    };

    if (alert.alertType === AlertType.Text) {
      data.includeText = true;
      data.smsMessage = alert?.message;
    } else if (alert.alertType === AlertType.Email) {
      data.includeEmail = true;
      data.emailMessage = alert.message;
    } else if (alert.alertType === AlertType.Voice) {
      data.includeVoice = true;
      data.voiceFileIdentifier = alert.voiceFileIdentifier;
      if (!alert.message) {
        // A recording
        data.voiceFileUrl = alert.voiceFileUrl;
      } else {
        // Text-To-Speech
        data.t2sMessage = alert.message;
        data.t2sUrl = alert.voiceFileUrl;
      }
    }

    dispatch("NEW_ALERT", data);
    navigate({ pathname: Path.Send, state: { from: from } });
  },

  copyAsDraft: function (alert) {
    try {
      let data = {
        id: 0, // new draft
        name: alert.name,
        isScheduled: false,
        scheduledDate: null,
        groups: alert.groups,
        allGroups: false,
        isEmergency: alert.isEmergency,
        isDraft: true,
        attachments: alert.attachments || [],
      };

      if (alert.alertType === AlertType.Text || alert.includeText) {
        data.includeText = true;
        data.smsMessage = alert.message || alert.smsMessage;
      }
      if (alert.alertType === AlertType.Email || alert.includeEmail) {
        data.includeEmail = true;
        data.emailMessage = alert.message || alert.emailMessage;
      }
      if (alert.alertType === AlertType.Voice || alert.includeVoice) {
        data.includeVoice = true;
        data.voiceFileIdentifier = alert.voiceFileIdentifier;
        if (!alert.message && alert.voiceFileUrl) {
          data.voiceFileUrl = alert.voiceFileUrl; // recording
        } else if (alert.message) {
          data.t2sMessage = alert.message; // TTS
          data.t2sUrl = alert.voiceFileUrl;
        }
      }

      dispatch("NEW_ALERT", data);
      navigate({
        pathname: Path.Send,
        state: { from: Path.Messages },
      });
      showToast("success", "Message copied as draft.");
    } catch (e) {
      console.error(e);
      showToast("error", "Unable to copy message. Please try again.");
    }
  },

  changeStep: function (step) {
    dispatch("ALERT_CHANGE_STEP", step);
  },

  saveAlert: async (alert) => {
    let token = appState().user.token;

    // If there's a recording, upload it before saving the Alert
    if (!alert.voiceFileIdentifier && alert.voiceRecording) {
      let uploadResult = await api.file.uploadRecording(
        token,
        alert.voiceRecording,
        alert.voiceRecording.type,
      );
      if (uploadResult.success) alert.voiceFileUrl = uploadResult.url;
      else return;
    }

    dispatch("ALERT_SAVING", true);
    let result = await api.alerts.save(token, alert);
    dispatch("ALERT_SAVING", false);

    if (result?.success) {
      dispatch("RESET_MY_ALERTS");

      if (alert.isDraft) {
        showToast("success", "Your Draft has been saved.");
        navigate(Path.Messages);
      } else if (alert.scheduledDate || (alert.scheduledDates || []).length > 0) {
        showToast("success", "Message Scheduled");
        navigate(Path.Messages);
      } else {
        showToast("success", "Preparing Message.");
        navigate(Path.Messages);
      }
    }
  },

  showDetails: function (alertId) {
    navigate("/alert?id=" + alertId);
    dispatch("NEW_ALERT", { ...blankAlert });
  },

  loadAlertDetails: async (alertId) => {
    let token = appState().user.token;
    let result = await api.alerts.getDetails(token, alertId);
    if (result.success) dispatch("NEW_ALERT", result);
  },

  messagePreview: async (alertId) => {
    let token = appState().user.token;
    let result = await api.alerts.messagePreview(token, alertId);
    if (result?.success) {
      dispatch("NEW_ALERT", result);
    }
  },

  getMessagePreviewEmailRecipientList: async (request) => {
    let token = appState().user.token;
    return await api.alerts.getMessagePreviewEmailRecipientList(token, request);
  },

  getMessagePreviewSmsRecipientList: async (request) => {
    let token = appState().user.token;
    return await api.alerts.getMessagePreviewSmsRecipientList(token, request);
  },

  getMessagePreviewVoiceRecipientList: async (request) => {
    let token = appState().user.token;
    return await api.alerts.getMessagePreviewVoiceRecipientList(token, request);
  },

  deleteAlert: async (alertId) => {
    let token = appState().user.token;
    dispatch("ALERT_SAVING", true);
    return await api.alerts.deleteAlert(token, alertId);
  },

  deleteTemplates: async (ids) => {
    let token = appState().user.token;
    return await api.alerts.deleteTemplates(token, ids);
  },

  duplicateTemplate: async (templateId) => {
    let token = appState().user.token;
    return await api.alerts.duplicateTemplate(token, templateId);
  },

  textToSpeech: async (message) => {
    let token = appState().user.token;
    return await api.file.textToSpeech(token, message);
  },

  loadTemplates: async (alertType) => {
    let token = appState().user.token;
    let result = await api.alerts.templates(token, alertType);
    if (result.success) {
      result.templates.forEach((t) => {
        t.value = t.id;
        t.label = t.name;
      });
      dispatch("LOAD_TEMPLATES", result.templates);
    }
  },

  // Voice message templates come in two distinct flavors that share the
  // same "Voice" message type on the Send Alert screen: pre-recorded audio
  // file templates (TemplateType.VOICE) and text-to-speech templates
  // (TemplateType.TTS). Load both and tag each item so the UI can react
  // appropriately once one is selected.
  loadVoiceTemplates: async () => {
    let token = appState().user.token;
    let [ttsResult, voiceResult] = await Promise.all([
      api.alerts.templates(token, TemplateType.TTS),
      api.alerts.templates(token, TemplateType.VOICE),
    ]);

    let combined = [];
    if (ttsResult.success) {
      combined = combined.concat(
        ttsResult.templates.map((t) => ({
          ...t,
          value: t.id,
          label: t.name,
          templateType: TemplateType.TTS,
        })),
      );
    }
    if (voiceResult.success) {
      combined = combined.concat(
        voiceResult.templates.map((t) => ({
          ...t,
          value: t.id,
          label: t.name,
          templateType: TemplateType.VOICE,
        })),
      );
    }

    dispatch("LOAD_VOICE_TEMPLATES", combined);
  },

  // Fetches full template detail, including the actual playable audio URL
  // for pre-recorded voice templates (see TemplateService.GetTemplateById).
  getTemplateById: async (templateId) => {
    let token = appState().user.token;
    return await api.alerts.getTemplateById(token, templateId);
  },
};
export default alerts;

export function alertReducer(state, action) {
  if (state === undefined) return initialState;

  switch (action.type) {
    case "NEW_ALERT":
    case "EMBED_NEW_ALERT":
      return {
        ...state,
        current: action.payload,
        step: 0,
      };

    case "ALERT_CHANGE_STEP":
      return {
        ...state,
        step: action.payload,
      };

    case "ALERT_SAVING":
      return {
        ...state,
        saving: action.payload,
      };

    case "UPDATE_NEW_ALERT":
      return {
        ...state,
        current: {
          ...state.current,
          ...action.payload,
        },
      };

    case "LOAD_TEMPLATES":
      return {
        ...state,
        templates: action.payload,
      };

    case "LOAD_VOICE_TEMPLATES":
      return {
        ...state,
        voiceTemplates: action.payload,
      };

    case "REMOVE_ATTACHMENT":
      return {
        ...state,
        current: {
          ...state.current,
          attachments: state.current.attachments.filter(
            (a) => a !== action.payload,
          ),
        },
      };

    case "LOGOUT":
      return initialState;

    default:
      return state;
  }
}

const blankAlert = {
  name: "",
  isScheduled: false,
  subject: "",
  htmlMessage: "",
  allGroups: false,
  groups: [],
  isEmergency: false,
  attachments: [],
};

const initialState = {
  current: { ...blankAlert },
  step: 0,
  templates: [],
  voiceTemplates: [],
  saving: false,
};
